import {AbstractControl, FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators} from "@angular/forms";
import {TranslatePipe} from "@ngx-translate/core";
import {Component, inject, Input, OnInit, OnDestroy} from "@angular/core";
import {SelectComponent} from "../../../shared/form/select/select.component";
import {SchuleDTO} from "../../../models/schule/SchuleDTO";
import {SchoolType} from "../../../models/schule/SchoolType";
import {forkJoin, Observable, of, Subject, switchMap, takeUntil, tap} from "rxjs";
import {SchuleService} from "../../../@core/services/schule/schule.service";
import {SchuljahrService} from "../../../@core/services/schuljahr/schuljahr.service";
import {SchuljahrDTO} from "../../../models/schuljahr/SchuljahrDTO";
import {KlasseDTO} from "../../../models/klasse/KlasseDTO";
import {BildungsgangDTO} from "../../../models/bildungsgang/BildungsgangDTO";
import {SchoolDataHelperService} from "../../../@core/services/schule/school-data-helper.service";
import {SchoolDataInfoDto} from "../../../models/student-ticket/list-view/SchoolDataInfoDto";
import {KlasseService} from "../../../@core/services/klasse/klasse.service";
import {AbstractFormModalComponent} from "../../../shared/modal/abstract-form-modal.component";
import {ModalShellComponent} from "../../../shared/modal/modal-shell/modal-shell.component";

interface SchoolDetailsForm {
  schoolId: FormControl<number | null>;
  schoolType: FormControl<SchoolType | null>;
  schoolYearId: FormControl<number | null>;
  classId: FormControl<number | null>;
  courseId: FormControl<number | null>;
}

@Component({
  selector: 'app-school-details-adjust-modal',
  templateUrl: './school-details-adjust-modal.component.html',
  styleUrls: ['./school-details-adjust-modal.component.scss'],
  imports: [
    TranslatePipe,
    ReactiveFormsModule,
    SelectComponent,
    ModalShellComponent,
  ],
  standalone: true
})
export class SchoolDetailsAdjustModalComponent extends AbstractFormModalComponent implements OnInit, OnDestroy {

  @Input() schoolDataInfo: SchoolDataInfoDto;

  protected form: FormGroup<SchoolDetailsForm>;
  protected validationErrorKey = 'SCHOOL_DATA.VALIDATION_ERROR';

  schuleList: Array<SchuleDTO> = [];
  landList = [];
  schuljahreList: SchuljahrDTO[] = [];
  filteredSchuljahreList: SchuljahrDTO[] = [];
  filteredKlassenList: KlasseDTO[] = [];
  filteredBildungsgangList: BildungsgangDTO[] = [];
  filteredSchoolLevelList = [];

  optionSchoolType;
  private readonly destroy$ = new Subject<void>();
  private isInitializing = false;

  private readonly fb = inject(FormBuilder);
  private readonly schuleService = inject(SchuleService);
  private readonly schuljahrService = inject(SchuljahrService);
  private readonly klasseService = inject(KlasseService);
  protected readonly schoolDataHelperService = inject(SchoolDataHelperService);

  ngOnInit(): void {

    this.loadLists().subscribe(() => {
      this.initForm();

      const schoolId = this.schoolDataInfo?.schuleId ? this.schoolDataHelperService.findIdByUuid(this.schuleList, 'schuleId', this.schoolDataInfo.schuleId) : null;

      this.isInitializing = true;

      this.form.patchValue({ schoolId });

      this.form.patchValue({
        schoolType: this.schoolDataInfo?.schulTyp as SchoolType,
        schoolYearId: this.schoolDataInfo?.schuljahrId ? this.schoolDataHelperService.findIdByUuid(this.schuljahreList, 'schuljahrId', this.schoolDataInfo.schuljahrId) : null,
        classId: this.schoolDataInfo?.klasseId ? this.schoolDataHelperService.findIdByUuid(this.filteredKlassenList, 'klasseId', this.schoolDataInfo.klasseId) : null,
        courseId: this.schoolDataInfo?.bildungsgangId ? this.schoolDataHelperService.findIdByUuid(this.filteredBildungsgangList, 'bildungsgangId', this.schoolDataInfo.bildungsgangId) : null,
      });

      this.isInitializing = false;

      if (schoolId) {
        this.updateConditionalValidators(schoolId);
      }
    });

    this.optionSchoolType = [{id: null, name: "-"},
      {id: "ELEMENTARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.ELEMENTARY")},
      {id: "GENERAL_SECONDARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.GENERAL_SECONDARY")},
      {id: "INTERMEDIATE_SECONDARY", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.INTERMEDIATE_SECONDARY")},
      {id: "GRAMMAR", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.GRAMMAR")},
      {id: "COMPREHENSIVE", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.COMPREHENSIVE")},
      {id: "VOCATIONAL", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.VOCATIONAL")},
      {id: "SPECIAL", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.SPECIAL")},
      {id: "OTHER", name: this.translateService.instant("TICKETDETAIL.SCHOOL_TYPE.OTHER")}
    ];
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initForm(): void {
    this.form = this.fb.group<SchoolDetailsForm>({
      schoolId: new FormControl(null, Validators.required),
      schoolType: new FormControl(null),
      schoolYearId: new FormControl(null, Validators.required),
      classId: new FormControl(null),
      courseId: new FormControl(null),
    });

    this.getFormItem('schoolId')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((id) => {
        const schuleDto = this.schuleList.find((schule) => schule.id === id);

        if (schuleDto) {
          this.filteredBildungsgangList = schuleDto.bildungsgaenge?.filter(t => t['archiv'] !== true) || [];
          this.filteredSchuljahreList = this.schuljahreList.filter(schuljahr => schuljahr.berufskolleg === (schuleDto?.schoolType === SchoolType.VOCATIONAL) && schuljahr['archiv'] !== true) || [];
        } else {
          this.filteredBildungsgangList = [];
          this.filteredSchuljahreList = [];
        }

        if (!this.isInitializing) {
          this.form.patchValue({
            schoolType: schuleDto ? schuleDto.schoolType : null,
            schoolYearId: null,
            classId: null,
            courseId: null,
          });
        } else {
          this.form.patchValue({
            schoolType: schuleDto ? schuleDto.schoolType : null,
          }, { emitEvent: false });
        }

        this.updateConditionalValidators(id);
      });
  }

  updateConditionalValidators(schoolId: number): void {
    const isVocational = this.isBerufskolleg(schoolId);
    const classControl = this.getFormItem('classId');
    const courseControl = this.getFormItem('courseId');

    if (isVocational) {
      courseControl?.setValidators([Validators.required]);
      classControl?.clearValidators();
    } else {
      classControl?.setValidators([Validators.required]);
      courseControl?.clearValidators();
    }

    classControl?.updateValueAndValidity();
    courseControl?.updateValueAndValidity();
  }

  getFormItem(s: string): AbstractControl {
    return this.form?.get(s);
  }

  getSchoolId(): number {
    return this.getFormItem('schoolId')?.value;
  }

  isBerufskolleg(id: number): boolean {
    return this.schoolDataHelperService.isBerufskolleg(this.schuleList, id);
  }

  private resolveNames(schoolYearId: number, classId: number, courseId: number): Pick<SchoolDataInfoDto, 'schuleName' | 'schuljahrName' | 'klasseName' | 'bildungsgangName'> {
    return {
      schuleName: this.schuleList.find(s => s.id === this.getSchoolId())?.name,
      schuljahrName: this.schuljahreList.find(s => s.id === schoolYearId)?.name,
      klasseName: this.filteredKlassenList.find(k => k.id === classId)?.name,
      bildungsgangName: this.filteredBildungsgangList.find(b => b.id === courseId)?.name,
    };
  }

  protected getCloseValue(): unknown {
    return this.buildSchoolDataDto();
  }

  override save(): void {
    if (this.form.valid) {
      this.activeModal.close(this.buildSchoolDataDto());
    } else {
      this.form.markAllAsTouched();
      this.customToastService.showError(
        this.translateService.instant("SCHOOL_DATA.VALIDATION_ERROR")
      );
    }
  }

  private buildSchoolDataDto(): SchoolDataInfoDto {
    const schoolYearId: number = this.getFormItem('schoolYearId')?.value;
    const classId: number = this.getFormItem('classId')?.value;
    const courseId: number = this.getFormItem('courseId')?.value;
    return {
      schuleId: this.schoolDataHelperService.findUuidById(this.schuleList, 'schuleId', this.getSchoolId()),
      schulTyp: this.getFormItem('schoolType')?.value,
      schuljahrId: this.schoolDataHelperService.findUuidById(this.schuljahreList, 'schuljahrId', schoolYearId),
      klasseId: this.schoolDataHelperService.findUuidById(this.filteredKlassenList, 'klasseId', classId),
      bildungsgangId: this.schoolDataHelperService.findUuidById(this.filteredBildungsgangList, 'bildungsgangId', courseId),
      ...this.resolveNames(schoolYearId, classId, courseId),
    };
  }

  loadLists(): Observable<any> {
    return this.schuleService.getSchulenForAntrag().pipe(
      switchMap(schulen => {
        this.schuleList = schulen;
        this.schuljahreList = [];

        let currentSchoolId = this.getFormItem('schoolId')?.value;
        if (!currentSchoolId && this.schoolDataInfo?.schuleId) {
          currentSchoolId = this.schoolDataHelperService.findIdByUuid(this.schuleList, 'schuleId', this.schoolDataInfo.schuleId);
        }

        const berufskollegFlag = currentSchoolId ? this.isBerufskolleg(currentSchoolId) : false;

        const schuljahreOhneBerufskolleg = this.schuljahrService.getSchuljahreBerufskolleg(true, false).pipe(
          tap(schuljahre => {
            this.schuljahreList = [...this.schuljahreList, ...schuljahre];
            this.filteredSchuljahreList = this.schuljahreList.filter(s => s.berufskolleg === berufskollegFlag);
          })
        );

        const schuljahreMitBerufskolleg = this.schuljahrService.getSchuljahreBerufskolleg(true, true).pipe(
          tap(schuljahre => {
            this.schuljahreList = [...this.schuljahreList, ...schuljahre];
            this.filteredSchuljahreList = this.schuljahreList.filter(s => s.berufskolleg === berufskollegFlag);
          })
        );

        let schoolLevelObservable = of([]);
        if (currentSchoolId) {
          schoolLevelObservable = this.schuleService.getSchoolLevelsForSchule(currentSchoolId).pipe(
            tap(levels => this.filteredSchoolLevelList = levels)
          );
        }

        const klassenObservable = this.klasseService.getKlassen(true).pipe(
          tap(klassen => {
            this.filteredKlassenList = [...klassen];
          })
        );

        return forkJoin([schuljahreOhneBerufskolleg, schuljahreMitBerufskolleg, schoolLevelObservable, klassenObservable]);
      })
    );
  }
}

